import { COLUMN_LEVEL_SEPARATOR } from '@opengov/reporting-types';
import type Quill from 'quill';

export interface QuillOp {
  insert?: string | Record<string, unknown>;
  delete?: number;
  retain?: number;
  attributes?: Record<string, unknown>;
}

export interface QuillDelta {
  ops: QuillOp[];
}

export const MATH_OPERATORS = {
  add: '+',
  subtract: '-',
  multiply: '*',
  divide: '/',
  openParen: '(',
  closeParen: ')'
} as const;

export type MathOperator = (typeof MATH_OPERATORS)[keyof typeof MATH_OPERATORS];

export const REFERENCE_PREFIX = '@';
export const REFERENCE_CHIP_BLOT_NAME = 'reference-chip';
export const REFERENCE_CHIP_CONTAINER_CLASS = 'reference-chip-container';
export const REFERENCE_CHIP_CLASS = 'reference-chip';
export const REFERENCE_CHIP_CLOSE_CLASS = 'reference-chip-close';

/**
 * Text kept after a chip so the caret has an editable node to sit in. With the caret straight after a
 * non-editable chip at the end of the line, Chromium and Safari drop the next keystrokes — a lost `+`
 * leaves `@a @b`. `buildFormulaString` collapses it, so it never reaches the stored formula.
 */
export const CHIP_TRAILING_TEXT = ' ';

/** Longest chip label rendered whole; past this the middle is elided. Sized to the formula field. */
export const CHIP_LABEL_MAX_LENGTH = 32;

/** Below this many characters a head is not worth keeping, so the label ellipsizes at the end. */
const MIN_CHIP_HEAD_LENGTH = 6;

/**
 * A chip label shortened to one line, elided in the MIDDLE rather than at the end.
 *
 * A column organized by several levels is named by its whole path, and it is the DEEPEST level that
 * tells two chips of one column group apart — trailing ellipsis would cut off exactly that and
 * leave the chips reading identically. So the last level is kept whole and the elision eats into
 * the levels before it. The untruncated label stays available as the chip's tooltip.
 */
export function truncateChipLabel(label: string, maxLength: number = CHIP_LABEL_MAX_LENGTH): string {
  if (label.length <= maxLength) {
    return label;
  }

  const lastSeparator = label.lastIndexOf(COLUMN_LEVEL_SEPARATOR);
  const tail = lastSeparator === -1 ? label.slice(-Math.ceil(maxLength / 2)) : label.slice(lastSeparator);
  const headLength = maxLength - tail.length - 1;

  if (headLength < MIN_CHIP_HEAD_LENGTH) {
    return `${label.slice(0, maxLength - 1).trimEnd()}\u2026`;
  }

  return `${label.slice(0, headLength).trimEnd()}\u2026${tail}`;
}

export type SelectableItem = {
  parentGroupId: string;
  groupId: string;
  label: string;
};

/** The value stored in each reference chip embed in Quill's delta. */
export type ReferenceChipValue = {
  parentGroupId: string;
  groupId: string;
  label: string;
};

/**
 * Extracts the inserted plain text from a Quill delta.
 * Returns an empty string if the delta has no text insert (e.g. delete or embed insert).
 */
export function getInsertedText(delta: QuillDelta): string {
  const insertOp = delta.ops.find((op) => typeof op.insert === 'string');
  return typeof insertOp?.insert === 'string' ? insertOp.insert : '';
}

/**
 * Returns the text typed after the "@" trigger character.
 * Uses getText(index, length) which correctly accounts for embed positions,
 * unlike getText() (no args) which omits embeds from the returned string.
 */
export function getSearchText({ quill, triggerIndex }: { quill: Quill; triggerIndex: number }): string {
  const searchStart = triggerIndex + 1; // +1 to skip the "@"
  const searchLength = quill.getLength() - searchStart - 1; // -1 for Quill's trailing \n
  return quill.getText(searchStart, searchLength);
}

/**
 * Returns the trigger index if "@" was just typed, or null if the insert isn't a trigger.
 */
export function resolveTriggerIndex({ delta, cursorIndex }: { delta: QuillDelta; cursorIndex: number }): number | null {
  const insertedText = getInsertedText(delta);
  if (insertedText !== REFERENCE_PREFIX) {
    return null;
  }
  return cursorIndex;
}

/**
 * Returns the menu position relative to the editor wrapper, or null if bounds can't be determined.
 */
export function resolveMenuPosition({ quill, triggerIndex }: { quill: Quill; triggerIndex: number }): {
  top: number;
  left: number;
} {
  const bounds = quill.getBounds(triggerIndex + 1) as { bottom: number } | null;
  // Left is pinned to 0 so the menu stays within the editor bounds as the cursor moves right.
  return { top: bounds?.bottom ?? 0, left: 10 };
}

/**
 * Reads the Quill editor contents and produces the formula string.
 * Text passes through as-is, reference chip embeds become @parentGroupId:groupId.
 */
export function buildFormulaString(quill: Quill): string {
  const delta = quill.getContents() as unknown as QuillDelta;
  let formula = '';

  for (const op of delta.ops) {
    if (typeof op.insert === 'string') {
      formula += op.insert;
    } else if (op.insert?.[REFERENCE_CHIP_BLOT_NAME]) {
      const ref = op.insert[REFERENCE_CHIP_BLOT_NAME] as ReferenceChipValue;
      formula += ` ${REFERENCE_PREFIX}${ref.parentGroupId}:${ref.groupId} `;
    }
  }

  return formula.replace(/\s+/g, ' ').trim();
}

/**
 * Deletes the "@" trigger and any search text, then inserts a reference chip embed at that position,
 * followed by `CHIP_TRAILING_TEXT` unless the chip already has it. The caret belongs at `triggerIndex + 2`.
 */
export function insertReferenceChip({
  quill,
  triggerIndex,
  searchTextLength,
  item
}: {
  quill: Quill;
  triggerIndex: number;
  searchTextLength: number;
  item: ReferenceChipValue;
}): void {
  const deleteLength = REFERENCE_PREFIX.length + searchTextLength;
  quill.deleteText(triggerIndex, deleteLength);
  quill.insertEmbed(triggerIndex, REFERENCE_CHIP_BLOT_NAME, item);

  const chipEnd = triggerIndex + 1;
  if (quill.getText(chipEnd, 1) !== CHIP_TRAILING_TEXT) {
    quill.insertText(chipEnd, CHIP_TRAILING_TEXT);
  }
}

/** Builds the @-prefixed reference used in formulas: `@parentGroupId:groupId`. */
export function buildReferenceKey({ parentGroupId, groupId }: { parentGroupId: string; groupId: string }): string {
  return `${REFERENCE_PREFIX}${parentGroupId}:${groupId}`;
}

/** Matches @parentGroupId:groupId patterns in a formula string. */
const FORMULA_REFERENCE_REGEX = /@([^@\s:]+):([^@\s:+*/()]+)/g;

/**
 * Parses a formula string into Quill-compatible ops.
 * Text passes through as-is, @parentGroupId:groupId references become embed ops.
 * Labels are looked up from options; falls back to groupId if not found.
 */
export function parseFormulaToOps({ formula, options }: { formula: string; options: SelectableItem[] }): QuillOp[] {
  if (!formula) {
    return [];
  }

  const labelLookup = new Map(options.map((option) => [`${option.parentGroupId}:${option.groupId}`, option.label]));
  const ops: QuillOp[] = [];
  let lastIndex = 0;

  FORMULA_REFERENCE_REGEX.lastIndex = 0;
  let match = FORMULA_REFERENCE_REGEX.exec(formula);

  while (match !== null) {
    // Add any text before this reference.
    if (match.index > lastIndex) {
      ops.push({ insert: formula.slice(lastIndex, match.index) });
    }

    const parentGroupId = match[1];
    const groupId = match[2];
    const label = labelLookup.get(`${parentGroupId}:${groupId}`) ?? groupId;

    ops.push({
      insert: { [REFERENCE_CHIP_BLOT_NAME]: { parentGroupId, groupId, label } }
    });

    lastIndex = match.index + match[0].length;
    match = FORMULA_REFERENCE_REGEX.exec(formula);
  }

  // Add any remaining text after the last reference — or, when the formula ends in one, the text
  // a chip needs after it before anything typed at the end can land (see CHIP_TRAILING_TEXT).
  if (lastIndex < formula.length) {
    ops.push({ insert: formula.slice(lastIndex) });
  } else if (lastIndex > 0) {
    ops.push({ insert: CHIP_TRAILING_TEXT });
  }

  return ops;
}

type FormulaReference = { parentGroupId: string; groupId: string };

/**
 * Extracts row/column references from a formula string.
 * Parses @parentGroupId:groupId patterns and deduplicates by composite key.
 */
export function parseFormulaReferences(formula: string): FormulaReference[] {
  const seen = new Set<string>();
  const references: FormulaReference[] = [];
  FORMULA_REFERENCE_REGEX.lastIndex = 0;
  let match = FORMULA_REFERENCE_REGEX.exec(formula);
  while (match !== null) {
    const ref = { parentGroupId: match[1], groupId: match[2] };
    const key = `${ref.parentGroupId}:${ref.groupId}`;
    if (!seen.has(key)) {
      seen.add(key);
      references.push(ref);
    }
    match = FORMULA_REFERENCE_REGEX.exec(formula);
  }
  return references;
}
